"use client";

import Image from "next/image";
import { StoreLink } from "@/components/store";
import { useTranslations } from "next-intl";
import type { NavigationItem } from "@cobrastyle/shared-types";
import type { SearchSuggestionsResult } from "@cobrastyle/storefront-core/actions";
import { buttonClassName, cn } from "@cobrastyle/ui";
import { PriceLabel } from "@/components/ui/price-label";
import type { BrandMenu } from "./navigation";
import type { BrandSuggestion } from "@/actions/brand-search";
import type { PopularSearchTerm } from "@/lib/popular-search-terms";
import { SearchContactText } from "@/components/search/search-contact-text";
import { MOBILE_HEADER_HEIGHT } from "./mobile-header-height";
import { MIN_QUERY_LENGTH } from "@/lib/search-constants";

// Helper function to highlight matching text
function HighlightedText({ text, query }: { text: string; query: string }) {
  if (!query.trim()) return <>{text}</>;

  const regex = new RegExp(
    `(${query.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`,
    "gi",
  );
  const parts = text.split(regex);

  return (
    <>
      {parts.map((part, i) =>
        regex.test(part) ? (
          <strong key={i} className="font-bold text-foreground">
            {part}
          </strong>
        ) : (
          <span key={i}>{part}</span>
        ),
      )}
    </>
  );
}

interface MobileSearchPanelProps {
  committedQuery: string;
  suggestions: SearchSuggestionsResult | null;
  brandMatches: BrandSuggestion[];
  productBrands: Record<string, string>;
  recentSearches: string[];
  saveRecentSearch: (term: string) => void;
  clearRecentSearch: (term: string) => void;
  clearAllRecentSearches: () => void;
  categories: NavigationItem[];
  brandMenu?: BrandMenu | null;
  popularSearchTerms: PopularSearchTerm[];
  onClose: () => void;
  onPopularTermClick: (term: PopularSearchTerm) => void;
  onGoToResults: (term: string) => void;
}

/**
 * The mobile search results/discovery panel — a drop-down below the header,
 * not a full-screen takeover: the header (logo, wishlist, cart) stays
 * visible and reachable, same positioning convention as the hamburger
 * drawer (mobile-navigation.tsx), which is why it shares that drawer's
 * `MOBILE_HEADER_HEIGHT` offset. All state/fetching lives in the trigger
 * (mobile-search-trigger.tsx); this component only renders what it's given.
 */
export function MobileSearchPanel({
  committedQuery,
  suggestions,
  brandMatches,
  productBrands,
  recentSearches,
  saveRecentSearch,
  clearRecentSearch,
  clearAllRecentSearches,
  categories,
  brandMenu,
  popularSearchTerms,
  onClose,
  onPopularTermClick,
  onGoToResults,
}: MobileSearchPanelProps) {
  const tc = useTranslations("common");
  const tSearch = useTranslations("search");
  const tBrands = useTranslations("brands");
  const tProduct = useTranslations("product");

  const suggestedCategories = suggestions?.categories ?? [];
  const suggestedProducts = suggestions?.products ?? [];
  const hasSuggestions =
    suggestedCategories.length > 0 || suggestedProducts.length > 0 || brandMatches.length > 0;
  const showInitialState =
    !committedQuery.trim() || committedQuery.trim().length < MIN_QUERY_LENGTH;
  const noResults =
    committedQuery.trim().length >= MIN_QUERY_LENGTH && suggestions && !hasSuggestions;
  const hasDiscoveryContent =
    recentSearches.length > 0 ||
    categories.length > 0 ||
    (brandMenu?.brands.length ?? 0) > 0 ||
    popularSearchTerms.length > 0;

  return (
    <>
      <div
        className="fixed inset-x-0 bottom-0 z-40 bg-black/30 backdrop-blur-sm"
        style={{ top: MOBILE_HEADER_HEIGHT }}
        onClick={onClose}
      />

      <div
        className="fixed inset-x-0 z-50 overflow-y-auto border-b border-border bg-background px-4 py-5"
        style={{ top: MOBILE_HEADER_HEIGHT, maxHeight: `calc(100vh - ${MOBILE_HEADER_HEIGHT}px)` }}
      >
        {/* Initial state — recent searches, then merchant-curated discovery
            content, matching the desktop bar's discovery row but stacked
            full-width instead of split into columns. */}
        {showInitialState && (
          <>
            {recentSearches.length > 0 && (
              <div className="mb-6">
                <div className="mb-2 flex items-center justify-between border-b-2 border-border pb-2.5">
                  <h3 className="font-mono text-[10px] uppercase tracking-[0.14em] text-accent-foreground">
                    {tSearch("recentSearches")}
                  </h3>
                  <button
                    onClick={clearAllRecentSearches}
                    className="text-xs text-muted transition-colors hover:text-foreground"
                  >
                    {tSearch("clearAll")}
                  </button>
                </div>
                {recentSearches.map((term) => (
                  <button
                    key={term}
                    onClick={() => onGoToResults(term)}
                    className="group flex w-full items-center gap-2.5 py-2.5 text-left"
                  >
                    <svg className="h-4 w-4 shrink-0 text-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span className="flex-1 text-sm text-foreground">{term}</span>
                    <span
                      role="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        clearRecentSearch(term);
                      }}
                      className="rounded p-1 text-muted"
                      aria-label={tc("cancel")}
                    >
                      <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </span>
                  </button>
                ))}
              </div>
            )}

            {popularSearchTerms.length > 0 && (
              <div className="mb-6">
                <h3 className="mb-2.5 border-b-2 border-border pb-2.5 font-mono text-[10px] uppercase tracking-[0.14em] text-accent-foreground">
                  {tSearch("popularTerms")}
                </h3>
                <div className="flex flex-wrap gap-2">
                  {popularSearchTerms.map((term) => (
                    <button
                      key={term.text}
                      type="button"
                      onClick={() => onPopularTermClick(term)}
                      className="rounded-full border border-border px-3.5 py-[9px] text-[13px] font-medium text-foreground transition-colors hover:border-accent-600 hover:text-accent-foreground"
                    >
                      {term.text}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {categories.length > 0 && (
              <div className="mb-6">
                <h3 className="mb-1 border-b-2 border-border pb-2.5 font-mono text-[10px] uppercase tracking-[0.14em] text-accent-foreground">
                  {tSearch("categories")}
                </h3>
                <ul>
                  {categories.slice(0, 8).map((category) => (
                    <li key={category.id}>
                      <StoreLink
                        href={`/${category.urlKey}`}
                        onClick={onClose}
                        className="flex items-center justify-between gap-2 border-b border-border/10 py-2.5 text-sm text-foreground"
                      >
                        <span className="truncate">{category.name}</span>
                        <span className="text-muted" aria-hidden="true">›</span>
                      </StoreLink>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {(brandMenu?.brands.length ?? 0) > 0 && (
              <div className="mb-6">
                <h3 className="mb-1 border-b-2 border-border pb-2.5 font-mono text-[10px] uppercase tracking-[0.14em] text-accent-foreground">
                  {tBrands("mainBrands")}
                </h3>
                <ul>
                  {brandMenu!.brands.map((brand) => (
                    <li key={brand.id}>
                      <StoreLink
                        href={`/${brand.urlKey}`}
                        onClick={onClose}
                        className="flex items-center justify-between gap-2 border-b border-border/10 py-2.5 text-sm text-foreground"
                      >
                        <span className="truncate">{brand.name}</span>
                        <span className="text-muted" aria-hidden="true">›</span>
                      </StoreLink>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Merchant contact fallback — same real phone/hours as the
                desktop bar's discovery panel. */}
            {popularSearchTerms.length > 0 && (
              <div className="rounded border border-border px-4 py-[18px]">
                <p className="mb-1.5 text-xs font-semibold uppercase tracking-wider text-muted">
                  {tSearch("helpTitle")}
                </p>
                <SearchContactText messageKey="helpDescription" className="text-sm text-foreground" />
                <StoreLink
                  href="/oppettider"
                  onClick={onClose}
                  className="mt-1.5 inline-block text-sm font-medium text-accent-foreground"
                >
                  {tSearch("helpOpeningHoursLink")} →
                </StoreLink>
              </div>
            )}

            {!hasDiscoveryContent && (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <svg className="mb-4 h-8 w-8 text-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5} aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <p className="text-sm text-muted">{tSearch("searchHint")}</p>
              </div>
            )}
          </>
        )}

        {/* No results */}
        {noResults && (
          <div className="py-8 text-center">
            <h3 className="mb-2 text-base font-semibold text-heading">
              {tSearch("noResults", { query: committedQuery })}
            </h3>
            <SearchContactText messageKey="noResultsCallUs" className="mx-auto max-w-sm text-sm text-muted" />
          </div>
        )}

        {/* Results */}
        {hasSuggestions && (
          <div>
            {suggestedProducts.length > 0 && (
              <div className="mb-6">
                <h3 className="mb-1 border-b-2 border-border pb-2.5 font-mono text-[10px] uppercase tracking-[0.14em] text-accent-foreground">
                  {tSearch("products")}
                </h3>
                {suggestedProducts.map((product) => (
                  <StoreLink
                    key={product.sku}
                    href={`/${product.urlKey}`}
                    onClick={() => {
                      saveRecentSearch(committedQuery);
                      onClose();
                    }}
                    className="grid grid-cols-[52px_1fr_auto] items-center gap-3 border-b border-border/50 py-2.5"
                  >
                    <div className="relative h-11 w-[52px] overflow-hidden rounded-sm bg-placeholder">
                      {product.thumbnail?.url && (
                        <Image
                          src={product.thumbnail.url}
                          alt={product.thumbnail.label || product.name}
                          fill
                          sizes="52px"
                          className="object-contain"
                        />
                      )}
                    </div>
                    <div className="min-w-0">
                      <p className="truncate text-[13px] font-semibold text-heading">
                        <HighlightedText text={product.name} query={committedQuery} />
                      </p>
                      <p className="truncate text-[11px] text-muted">
                        {productBrands[product.sku] && <>{productBrands[product.sku]} · </>}
                        {tProduct("sku")} {product.sku}
                      </p>
                    </div>
                    <PriceLabel
                      price={product.price.final}
                      originalPrice={
                        product.price.discount &&
                        product.price.discount.amount > 0 &&
                        product.price.regular > product.price.final
                          ? product.price.regular
                          : undefined
                      }
                      currency={product.price.currency}
                      size="sm"
                    />
                  </StoreLink>
                ))}
              </div>
            )}

            {suggestedCategories.length > 0 && (
              <div className="mb-6">
                <h3 className="mb-2.5 border-b-2 border-border pb-2.5 font-mono text-[10px] uppercase tracking-[0.14em] text-accent-foreground">
                  {tSearch("categories")}
                </h3>
                <div className="flex flex-wrap gap-2">
                  {suggestedCategories.map((cat) => (
                    <StoreLink
                      key={cat.urlKey}
                      href={`/${cat.urlPath}`}
                      onClick={onClose}
                      className="rounded-full border border-border px-3.5 py-[7px] text-xs font-semibold text-foreground"
                    >
                      <HighlightedText text={cat.name} query={committedQuery} />
                    </StoreLink>
                  ))}
                </div>
              </div>
            )}

            {brandMatches.length > 0 && (
              <div className="mb-6">
                <h3 className="mb-2.5 border-b-2 border-border pb-2.5 font-mono text-[10px] uppercase tracking-[0.14em] text-accent-foreground">
                  {tSearch("brands")}
                </h3>
                <div className="flex flex-wrap gap-2">
                  {brandMatches.map((brand) => (
                    <StoreLink
                      key={brand.id}
                      href={`/${brand.urlKey}`}
                      onClick={onClose}
                      className="rounded-full border border-border px-3.5 py-[7px] text-xs font-semibold text-foreground"
                    >
                      <HighlightedText text={brand.name} query={committedQuery} />
                    </StoreLink>
                  ))}
                </div>
              </div>
            )}

            {committedQuery.trim() && (
              <button
                onClick={() => onGoToResults(committedQuery.trim())}
                className={cn(buttonClassName({ variant: "primary" }), "w-full justify-center")}
              >
                {tSearch("viewAllResults", { query: committedQuery })}
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                </svg>
              </button>
            )}
          </div>
        )}
      </div>
    </>
  );
}
